import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button } from '@mui/material';
import { GeneralUpload } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { networkCreateFootbalopGameCard, networkUpdateFootbalopGameCard } from 'src/network/footballop/footballop-game-card';
import { FOOTBALLOP_GAME_CARD_LIST } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

const GameCardForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const gameCardDetails = modal?.data;
  const isEditMode = !!gameCardDetails;

  const defaultValues = getDefaultValues(gameCardDetails);

  const methods = useForm({
    resolver: yupResolver(schema),
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      FootbalopGameCardApplicationID: 30,
      FootbalopGameCardAttak: +values.FootbalopGameCardAttak,
      FootbalopGameCardDefence: +values.FootbalopGameCardDefence,
      FootbalopGameCardSpeed: +values.FootbalopGameCardSpeed,
      FootbalopGameCardOverall: +values.FootbalopGameCardOverall,
      FootbalopGameCardPrice: +values.FootbalopGameCardPrice,
      FootbalopGameCardStartKit: +values.FootbalopGameCardStartKit,
      FootbalopGameCardVersion: +values.FootbalopGameCardVersion,
    };
    try {
      if (isEditMode) {
        const res = await networkUpdateFootbalopGameCard(gameCardDetails?.FootbalopGameCardID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('کارت موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [FOOTBALLOP_GAME_CARD_LIST], refetchType: 'all' });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateFootbalopGameCard(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('کارت موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [FOOTBALLOP_GAME_CARD_LIST], refetchType: 'all' });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // --------------------------------------------------------------------

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="FootbalopGameCardName" type="text" label="نام بازیکن" />

              <GeneralUpload
                label="تصویر"
                id="card_image"
                name="FootbalopGameCardImages"
                afterChangeAction={(res) => {
                  setValue('FootbalopGameCardImages', res);
                  trigger('FootbalopGameCardImages');
                }}
              />

              <RHFTextField name="FootbalopGameCardNation" type="text" label="ملیت" />
              <RHFTextField name="FootbalopGameCardTeam" type="text" label="تیم" />

              <RHFTextField name="FootbalopGameCardSeason" type="text" label="فصل" />
              <RHFTextField name="FootbalopGameCardPostion" type="text" label="پست" />

              <RHFTextField name="FootbalopGameCardAttak" type="text" label="حمله" />
              <RHFTextField name="FootbalopGameCardDefence" type="text" label="دفاع" />

              <RHFTextField name="FootbalopGameCardSpeed" type="text" label="سرعت" />
              <RHFTextField name="FootbalopGameCardOverall" type="text" label="امتیاز کلی" />

              <RHFTextField name="FootbalopGameCardPrice" type="text" label="قیمت" />
              <RHFTextField name="FootbalopGameCardStartKit" type="text" label="کیت شروع" />

              <RHFTextField name="FootbalopGameCardNumber" type="text" label="شماره کارت" />
              <RHFTextField name="FootbalopGameCardVersion" type="text" label="نسخه کارت" />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GameCardForm;
